/**
 * Acciones de facturación en la top bar (3PL): creación y operaciones contables
 * según accounting_supplier (holded | contalink).
 */

import { faCloudArrowUp, faFileCheck, faFileInvoice, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface InvoicesTopBarActionsProps {
  showHoldedActions: boolean;
  showContalinkActions: boolean;
  onCreateHoldedInvoice: () => void;
  onCreateManualInvoice: () => void;
  onGenerateHolded: () => void;
  onConfirmHolded: () => void;
  onRegisterContalink: () => void;
  isLoadingPending: boolean;
  isLoadingPendingConfirm: boolean;
  isLoadingContalinkPending: boolean;
  isGenerating: boolean;
  isConfirming: boolean;
}

export const InvoicesTopBarActions: FC<InvoicesTopBarActionsProps> = ({
  showHoldedActions,
  showContalinkActions,
  onCreateHoldedInvoice,
  onCreateManualInvoice,
  onGenerateHolded,
  onConfirmHolded,
  onRegisterContalink,
  isLoadingPending,
  isLoadingPendingConfirm,
  isLoadingContalinkPending,
  isGenerating,
  isConfirming,
}) => {
  const { t } = useTranslation('invoicesModule');

  const generateDisabled = isGenerating || isConfirming || isLoadingPending;
  const confirmDisabled = isGenerating || isConfirming || isLoadingPendingConfirm;

  const generateLabel = useMemo(() => {
    if (isLoadingPending) return t('buttons.holded.loading', 'Cargando...');
    if (isGenerating) return t('buttons.holded.generating', 'Generando...');
    return t('buttons.holded.generate', 'Generar en Holded');
  }, [isLoadingPending, isGenerating, t]);

  const confirmLabel = useMemo(() => {
    if (isLoadingPendingConfirm) return t('buttons.holded.loading', 'Cargando...');
    if (isConfirming) return t('buttons.holded.confirming', 'Confirmando...');
    return t('buttons.holded.confirm', 'Confirmar en Holded');
  }, [isLoadingPendingConfirm, isConfirming, t]);

  if (!showHoldedActions) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {showContalinkActions && (
          <Button
            variant="outline"
            className="gap-1.5 border-card-border bg-card-bg"
            onClick={onRegisterContalink}
            disabled={isLoadingContalinkPending}
            data-testid="header-action-register-contalink"
          >
            <FontAwesomeIcon
              icon={faFileInvoice}
              className={`size-3.5 ${isLoadingContalinkPending ? 'animate-pulse' : ''}`}
            />
            <span className="hidden sm:inline">
              {isLoadingContalinkPending
                ? t('buttons.contalink.loading', 'Cargando...')
                : t('buttons.contalink.register', 'Registrar en Contalink')}
            </span>
            <span className="sm:hidden">Contalink</span>
          </Button>
        )}
        <Button
          variant="primary"
          className="gap-1.5"
          onClick={onCreateManualInvoice}
          data-testid="header-action-create-manual-invoice"
        >
          <FontAwesomeIcon icon={faPlus} className="size-3.5" />
          <span className="hidden sm:inline">{t('buttons.manual_invoice.create', 'Crear Factura Manual')}</span>
          <span className="sm:hidden">{t('buttons.topbar.create_short', 'Crear')}</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="primary" className="gap-1.5 pr-2" data-testid="header-invoices-create-menu">
            <FontAwesomeIcon icon={faPlus} className="size-3.5" />
            <span className="hidden sm:inline">{t('buttons.topbar.create_invoice', 'Crear factura')}</span>
            <span className="sm:hidden">{t('buttons.topbar.create_short', 'Crear')}</span>
            <ChevronDown className="size-4 opacity-80" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[14rem] border-card-border bg-card-bg">
          <DropdownMenuItem
            onClick={onCreateHoldedInvoice}
            className="cursor-pointer gap-2"
            data-testid="header-action-create-holded-invoice"
          >
            <FontAwesomeIcon icon={faPlus} className="size-3.5 text-muted-foreground" />
            {t('buttons.holded_invoice.create', 'Crear Factura en Holded')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onCreateManualInvoice}
            className="cursor-pointer gap-2"
            data-testid="header-action-create-manual-invoice"
          >
            <FontAwesomeIcon icon={faPlus} className="size-3.5 text-muted-foreground" />
            {t('buttons.manual_invoice.create', 'Crear Factura Manual')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="gap-1.5 border-card-border bg-card-bg pr-2"
            data-testid="header-invoices-holded-menu"
          >
            <span className="hidden sm:inline">{t('buttons.topbar.holded_menu', 'Operaciones Holded')}</span>
            <span className="sm:hidden">Holded</span>
            <ChevronDown className="size-4 opacity-80" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[14rem] border-card-border bg-card-bg">
          <DropdownMenuItem
            onClick={onGenerateHolded}
            disabled={generateDisabled}
            className="cursor-pointer gap-2"
            data-testid="header-action-generate-holded"
          >
            <FontAwesomeIcon
              icon={faCloudArrowUp}
              className={`size-3.5 text-muted-foreground ${isGenerating || isLoadingPending ? 'animate-pulse' : ''}`}
            />
            {generateLabel}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onConfirmHolded}
            disabled={confirmDisabled}
            className="cursor-pointer gap-2"
            data-testid="header-action-confirm-holded"
          >
            <FontAwesomeIcon
              icon={faFileCheck}
              className={`size-3.5 text-muted-foreground ${isConfirming || isLoadingPendingConfirm ? 'animate-pulse' : ''}`}
            />
            {confirmLabel}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
